import React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, CalendarClock, Wallet } from "lucide-react";
import { FollowUpList } from "../../components/FollowUpList";
import { FollowUpVisitsReport } from "../../components/FollowUpVisitsReport";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";

/**
 * Phase 105 — "top balances due" list for the signed-in tax collector.
 * Phase 196 — second tab: my scheduled follow-up VISITS (the full list with Done / Reschedule, status, Repeated badge and
 * Excel export) — the same rows the Supervisor and Admin see in Reports → Follow-up Visits, limited to my own entries.
 */
export default function FollowUp() {
  const { code } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "visits" ? "visits" : "balances";
  return (
    <div className="space-y-4" data-testid="collector-follow-up-page">
      <div>
        <Link to={`/${code}/collector`} className="flex items-center gap-1 text-sm text-muted-foreground" data-testid="follow-up-back-link">
          <ArrowLeft className="h-4 w-4" /> Today
        </Link>
        <h1 className="text-xl font-semibold font-heading flex items-center gap-2 mt-1"><Wallet className="h-5 w-5 text-destructive" /> Follow-Up</h1>
        <p className="text-sm text-muted-foreground">Balances due in your area, and the return visits you have scheduled.</p>
      </div>
      <Tabs value={tab} onValueChange={(v) => setParams(v === "visits" ? { tab: "visits" } : {})}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="balances" data-testid="follow-up-tab-balances"><Wallet className="h-3.5 w-3.5 mr-1.5" /> Balances due</TabsTrigger>
          <TabsTrigger value="visits" data-testid="follow-up-tab-visits"><CalendarClock className="h-3.5 w-3.5 mr-1.5" /> My follow-up visits</TabsTrigger>
        </TabsList>
        <TabsContent value="balances" className="mt-4">
          <p className="text-xs text-muted-foreground mb-3">Every property in your wards, mohallas and assignments that still owes money — highest balance first. Tap one to collect or record a visit.</p>
          <FollowUpList endpoint={`/t/${code}/field/follow-up`} exportEndpoint={`/t/${code}/field/follow-up/export`} exportName={`${code}_my_follow_up`}
            propertyPath={(r) => `/${code}/collector/property/${r.id}`} testId="collector-follow-up" />
        </TabsContent>
        <TabsContent value="visits" className="mt-4">
          <p className="text-xs text-muted-foreground mb-3">Return visits you scheduled while logging field visits. Mark each one Done with a reason (Owner Absent, House Locked, Payment Promised, Partial Payment Collected, Dispute or Complaint, Other) or reschedule it — every move is logged. Overdue ones stay listed until closed.</p>
          <FollowUpVisitsReport code={code} role="collector" testId="collector-fu" canAct
            propertyPath={(r) => `/${code}/collector/property/${r.property_id}`} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
